/**
 * BiomeMap — strict per-pixel surface biome assignment.
 *
 * Each cosmology pixel = 40×40 game tiles. The surface line (pixel y=57)
 * spans pixel x=9 to x=63 (55 pixels). Each pixel is assigned exactly
 * one biome word index. Where a zone has fewer biomes than pixels, a
 * biome is given to multiple pixels.
 *
 * The gradient follows the 4-part biome assignment from the cosmology docs:
 *
 *   px 9-18   EAST (Dragon)    outer→inner  10 biomes, 10 pixels
 *   px 19-20  E–S transition   mama          1 biome,   2 pixels
 *   px 21-29  SOUTH (Phoenix)  outer→inner   9 biomes,  9 pixels
 *   px 30     S–C transition   olin          1 biome,   1 pixel
 *   px 31-41  CENTER           left→right   11 biomes, 11 pixels
 *   px 42     C–W transition   jo            1 biome,   1 pixel
 *   px 43-51  WEST (Lion)      inner→outer   9 biomes,  9 pixels
 *   px 52     W–N transition   alasa         1 biome,   1 pixel
 *   px 53-63  NORTH (Turtle)   inner→outer   9 biomes, 11 pixels
 *
 * Only surface biomes are assigned for now. Underground, sky, and other
 * zone biomes will be added later.
 */

import { BiomeZone, BIOME_PALETTES } from './constants';
import { PIXELS_TO_TILES, CENTER_PX } from './CosmologyMap';

// ── Pixel Constants ───────────────────────────────────────────────────

/** First surface pixel X (grass surface leftmost). */
const SURFACE_PIXEL_START = 9;

/** Last surface pixel X (grass surface rightmost, inclusive). */
const SURFACE_PIXEL_END = 63;

// ── Per-Pixel Biome Array ─────────────────────────────────────────────
// Index 0 = pixel 9, index 54 = pixel 63. Each entry is a word index.

const SURFACE_BIOME_PIXELS: number[] = [
    // ── East (Dragon): outer→inner — px 9-18, 10 biomes ──
    91,  // px  9: ma sijelo — Flesh Mire (borders ocean)
    1,   // px 10: ma akesi — Serpent Marsh
    6,   // px 11: ma ante — Tremor Flats (zone namesake)
    62,  // px 12: ma nasa — Fungal Bloom
    30,  // px 13: ma kule — Prismatic Springs
    24,  // px 14: ma ken — Mist Thicket
    41,  // px 15: ma linja — Vine Choke
    78,  // px 16: ma pilin — Glowmoss Glade
    74,  // px 17: ma palisa — Bamboo Thicket
    22,  // px 18: ma kama — Seedling Waste (nearest center)

    // ── East–South transition — px 19-20, mama ×2 ──
    49,  // px 19: ma mama — Nesting Basin
    49,  // px 20: ma mama — Nesting Basin

    // ── South (Phoenix): outer→inner — px 21-29, 9 biomes ──
    87,  // px 21: ma seli — Magma Field (borders East)
    93,  // px 22: ma sin — Volcanic Newland
    43,  // px 23: ma loje — Red Dust Basin
    111, // px 24: ma unpa — Lush Thicket
    76,  // px 25: ma pana — Geyser Flat
    18,  // px 26: ma jelo — Golden Savanna
    102, // px 27: ma suwi — Honey Glade
    75,  // px 28: ma pan — Grain Field
    26,  // px 29: ma kili — Fruiting Grove (nearest center)

    // ── South–Center transition — px 30, olin ──
    69,  // px 30: ma olin — Paradise Garden

    // ── Center: left→right — px 31-41, 11 biomes ──
    36,  // px 31: ma lawa — Throne Ruin (left edge)
    71,  // px 32: ma open — Spawn Clearing (spawn at tile -160)
    48,  // px 33: ma ma — Heartland
    7,   // px 34: ma anu — Biome Border
    17,  // px 35: ma jan — Settlement
    23,  // px 36: ma kasi — Heartwood Grove (World Tree at tile 0)
    15,  // px 37: ma insa — Tree Hollow
    63,  // px 38: ma nasin — Pilgrims' Road
    9,   // px 39: ma e — Tilled Soil
    11,  // px 40: ma esun — Trade Crossroads
    108, // px 41: ma toki — Babel Ruin (right edge)

    // ── Center–West transition — px 42, jo ──
    19,  // px 42: ma jo — Vein Valley

    // ── West (Lion): inner→outer — px 43-51, 9 biomes ──
    101, // px 43: ma supa — Plateau (nearest center)
    73,  // px 44: ma pali — Slag Heap
    42,  // px 45: ma lipu — Mesa
    14,  // px 46: ma ilo — Quarry
    28,  // px 47: ma ko — Sand Flat
    88,  // px 48: ma selo — Canyon Rim
    95,  // px 49: ma sinpin — Cliff Face
    27,  // px 50: ma kiwen — Stone Barrens (zone namesake)
    113, // px 51: ma utala — Battlefield (borders North)

    // ── West–North transition — px 52, alasa ──
    3,   // px 52: ma alasa — Hunting Steppe

    // ── North (Turtle): inner→outer — px 53-63, 9 biomes in 11 pixels ──
    106, // px 53: ma telo — Wetland (nearest center, 2 px)
    106, // px 54: ma telo — Wetland
    34,  // px 55: ma lape — Ruin Meadow
    32,  // px 56: ma kute — Wind Hollow
    97,  // px 57: ma sona — Standing Stones (zone namesake)
    8,   // px 58: ma awen — Petrified Forest
    86,  // px 59: ma sama — Mirror Lake
    114, // px 60: ma walo — Salt Flat
    118, // px 61: ma weka — Eroded Waste
    38,  // px 62: ma lete — Tundra (borders ocean, 2 px)
    38,  // px 63: ma lete — Tundra
];

// ── Biome English Names ───────────────────────────────────────────────
// All 120 biome names keyed by word index. From cosmology-biome-proximity-mapping.md.

export const BIOME_NAMES: Record<number, string> = {
    0:   'Root Altar',
    1:   'Serpent Marsh',
    2:   'The Void',
    3:   'Hunting Steppe',
    4:   'Celestial Sphere',
    5:   'Abyss Floor',
    6:   'Tremor Flats',
    7:   'Biome Border',
    8:   'Petrified Forest',
    9:   'Tilled Soil',
    10:  'Red River Ford',
    11:  'Trade Crossroads',
    12:  'Open Plains',
    13:  'Blighted Waste',
    14:  'Quarry',
    15:  'Tree Hollow',
    16:  'Tar Bog',
    17:  'Settlement',
    18:  'Golden Savanna',
    19:  'Vein Valley',
    20:  'Coral Shallows',
    21:  'Echo Cavern',
    22:  'Seedling Waste',
    23:  'Heartwood Grove',
    24:  'Mist Thicket',
    25:  'Ore Vein',
    26:  'Fruiting Grove',
    27:  'Stone Barrens',
    28:  'Sand Flat',
    29:  'Cloud Sea',
    30:  'Prismatic Springs',
    31:  'Prison Colony',
    32:  'Wind Hollow',
    33:  'Gate Passage',
    34:  'Ruin Meadow',
    35:  'Deep Water',
    36:  'Throne Ruin',
    37:  'Frost Cavern',
    38:  'Tundra',
    39:  'Fault Line',
    40:  'Moss Garden',
    41:  'Vine Choke',
    42:  'Mesa',
    43:  'Red Dust Basin',
    44:  'Mountain Pass',
    45:  'Chasm Ledge',
    46:  'Overlook',
    47:  'Cave Mouth',
    48:  'Heartland',
    49:  'Nesting Basin',
    50:  'Shallows Pasture',
    51:  'Fertile Crescent',
    52:  'Starfield',
    53:  'Warrior Crypt',
    54:  'Barren Table',
    55:  'Boneyard',
    56:  'Hinterland',
    57:  'Migration Trail',
    58:  'Moonscape',
    59:  'Arena',
    60:  'Archipelago',
    61:  'Crystal Grid',
    62:  'Fungal Bloom',
    63:  'Pilgrims\' Road',
    64:  'Sacred Peak',
    65:  'Hall of the Present',
    66:  'White River Banks',
    67:  'Valley Floor',
    68:  'Ordeal Pit',
    69:  'Paradise Garden',
    70:  'Foreign Shore',
    71:  'Spawn Clearing',
    72:  'Crater Field',
    73:  'Slag Heap',
    74:  'Bamboo Thicket',
    75:  'Grain Field',
    76:  'Geyser Flat',
    77:  'Alpine Meadow',
    78:  'Glowmoss Glade',
    79:  'Shadow Reach',
    80:  'Fossil Shelf',
    81:  'Hive Mound',
    82:  'Black River Shore',
    83:  'Basin',
    84:  'Blessed Ground',
    85:  'Shrine Terrace',
    86:  'Mirror Lake',
    87:  'Magma Field',
    88:  'Canyon Rim',
    89:  'Aurora Veil',
    90:  'Floating Isle',
    91:  'Flesh Mire',
    92:  'Ring Formation',
    93:  'Volcanic Newland',
    94:  'Counter-World',
    95:  'Cliff Face',
    96:  'Geoglyph Plain',
    97:  'Standing Stones',
    98:  'Magenta River',
    99:  'Titan\'s Rest',
    100: 'Solar Terrace',
    101: 'Plateau',
    102: 'Honey Glade',
    103: 'Origin Depth',
    104: 'Lone Island',
    105: 'Nomad\'s Corridor',
    106: 'Wetland',
    107: 'Strata Shelf',
    108: 'Babel Ruin',
    109: 'Ruin City',
    110: 'Continental Divide',
    111: 'Lush Thicket',
    112: 'River Mouth',
    113: 'Battlefield',
    114: 'Salt Flat',
    115: 'Lonely Peak',
    116: 'Nesting Cliff',
    117: 'Maelstrom',
    118: 'Eroded Waste',
    119: 'Siren Shore',
};

/**
 * Get the English name for a biome by word index.
 * Returns the name or undefined if no name is registered.
 */
export function getBiomeName(wordIndex: number): string | undefined {
    return BIOME_NAMES[wordIndex];
}

// ── Lookup Functions ──────────────────────────────────────────────────

/**
 * Get the surface biome word index at a given cosmology pixel X.
 * Returns the word index (0-119) for pixels 9-63, or -1 if outside.
 */
export function getSurfaceBiomeAtPixelX(px: number): number {
    const idx = px - SURFACE_PIXEL_START;
    if (idx < 0 || idx >= SURFACE_BIOME_PIXELS.length) return -1;
    return SURFACE_BIOME_PIXELS[idx];
}

/**
 * Get the surface biome word index at a given tile X position.
 * Converts tile X to pixel X, then looks up the per-pixel assignment.
 * Returns -1 if outside the surface range.
 */
export function getSurfaceBiomeAtX(tileX: number): number {
    const px = Math.floor(tileX / PIXELS_TO_TILES) + CENTER_PX;
    return getSurfaceBiomeAtPixelX(px);
}

/**
 * Get the biome word index at a given tile coordinate.
 *
 * For now, only surface biomes are assigned — this returns the surface
 * biome based on tile X position regardless of Y. Underground, sky,
 * and other zone biomes will be added in future updates.
 *
 * Returns the word index (0-119) if a biome is assigned, or -1 if the
 * tile is outside all assigned biome zones.
 */
export function getBiomeAtTile(tileX: number, _tileY: number): number {
    return getSurfaceBiomeAtX(tileX);
}

/**
 * Get the BiomeZone for a given biome word index.
 * Looks up the word in BIOME_PALETTES to find its zone assignment.
 * Returns null if the word index is not found.
 */
export function getBiomeZone(wordIndex: number): BiomeZone | null {
    const entry = BIOME_PALETTES.find(e => e.word === wordIndex);
    return entry ? entry.zone : null;
}

/**
 * Build a reverse lookup: word index → BIOME_PALETTES array index.
 * Used by PaletteScene to scroll to the current biome.
 */
const _wordToPaletteIndex = new Map<number, number>();
for (let i = 0; i < BIOME_PALETTES.length; i++) {
    _wordToPaletteIndex.set(BIOME_PALETTES[i].word, i);
}

/**
 * Get the index into the BIOME_PALETTES array for a given biome word.
 * Returns -1 if not found.
 */
export function getBiomePaletteIndex(wordIndex: number): number {
    return _wordToPaletteIndex.get(wordIndex) ?? -1;
}
